<template>
  <EditorTabFrame
    :loading="loading"
    :loaded="loaded"
    :load-error="loadError"
    :saving="saving"
    :dirty="isDirty"
    :conflict-message="conflictMessage"
    @save="save"
    @discard="discard"
    @reload="load"
  >
    <div v-if="form">
      <EditorSection title="Números da sorte" description="Distribuição de números para sorteio entre os doadores.">
        <EditorSwitch v-model="form.activeDraw" label="Distribuir números da sorte nesta campanha" />
        <div v-if="form.activeDraw" class="ed-grid mt-3">
          <div>
            <v-select v-model="form.drawModeId" :items="drawModes" label="Como distribuir"></v-select>
          </div>
          <div v-if="form.drawModeId === DrawModes.ByValue">
            <InputMoney
              v-model="form.valueDonationByNumberDraw"
              textLabel="Valor doado por número"
              :precision="0"
              :required="false"
              :autoFocusOnValidationFail="false"
            />
          </div>
          <div v-else class="ed-help ed-draw-note">
            Por faixa: cada produto de doação define quantos números dá (campo no cadastro do produto).
            <div v-if="productsWithoutQuantity > 0" class="warning--text mt-1">
              {{ productsWithoutQuantity }} produto(s) ativo(s) sem quantidade não darão números.
            </div>
          </div>
        </div>
      </EditorSection>

      <EditorSection title="Produtos" description="Categorias de doação oferecidas no checkout.">
        <div class="ed-grid">
          <div>
            <InputText v-model="form.productCall" type="text" textLabel="Chamada exibida acima dos produtos" place-holder="Ex.: Escolha sua categoria de doação" :required="false" />
          </div>
        </div>
        <EditorProductList
          title="Produtos da campanha"
          new-label="Novo produto"
          :items="products"
          :show-draw-quantity="form.activeDraw && form.drawModeId === DrawModes.ByProduct"
          @new="openProduct(null)"
          @edit="openProduct"
          @toggle="toggleProduct"
        />
      </EditorSection>

      <EditorSection title="Brindes" description="Contrapartidas oferecidas a partir de determinados valores.">
        <template slot="aside">
          <v-btn flat small color="primary" class="ed-text-btn" @click="giftConfigDialog = true">
            <i class="fal fa-cog mr-2"></i>{{ hasGiftConfig ? "Configuração de brindes" : "Configurar brindes" }}
          </v-btn>
        </template>
        <EditorProductList
          title="Brindes da campanha"
          new-label="Novo brinde"
          :items="gifts"
          @new="openGift(null)"
          @edit="openGift"
          @toggle="toggleGift"
        />
      </EditorSection>

      <NewGiftConfig v-if="giftConfigDialog" :campaignId="String(campaignId)" :giftConfigDialog="giftConfigDialog" @openGiftDialog="onGiftConfigClosed" />
      <NewCampaignProduct
        v-if="productDialog"
        :campaignId="campaignId"
        :dialog="productDialog"
        :toEdit="productToEdit"
        :drawByProduct="form.activeDraw && form.drawModeId === DrawModes.ByProduct"
        @close="onModalClosed('productDialog')"
      />
      <NewCampaignGift
        v-if="giftDialog"
        :campaignId="campaignId"
        :dialog="giftDialog"
        :toEdit="giftToEdit"
        @close="onModalClosed('giftDialog')"
      />
    </div>
  </EditorTabFrame>
</template>

<script>
import editorTab from "../editorTab.mixin";
import EditorTabFrame from "../EditorTabFrame.vue";
import EditorSection from "../EditorSection.vue";
import EditorSwitch from "../EditorSwitch.vue";
import EditorProductList from "../EditorProductList.vue";
import InputText from "@/components/input/InputText.vue";
import InputMoney from "@/components/input/InputMoney.vue";
import NewCampaignProduct from "@/components/NewCampaignProduct.vue";
import NewCampaignGift from "@/components/NewCampaignGift.vue";
import NewGiftConfig from "@/components/NewGiftConfig.vue";
import CampaignEditorService from "@/scripts/services/campaignEditor.service";
import CampaignService from "@/scripts/services/campaign.service";

const service = new CampaignEditorService();
const legacyService = new CampaignService();
const DrawModes = Object.freeze({ ByValue: 1, ByProduct: 2 });

export default {
  name: "ProductsTab",
  mixins: [editorTab],
  components: { EditorSection, EditorTabFrame, EditorSwitch, EditorProductList, InputText, InputMoney, NewCampaignProduct, NewCampaignGift, NewGiftConfig },
  data() {
    return {
      DrawModes,
      drawModes: Object.freeze([
        { value: DrawModes.ByValue, text: "Por valor doado" },
        { value: DrawModes.ByProduct, text: "Por faixa de doação (produto)" },
      ]),
      products: [],
      gifts: [],
      hasGiftConfig: false,
      productsWithoutQuantity: 0,
      productDialog: false,
      giftDialog: false,
      productToEdit: null,
      giftToEdit: null,
      giftConfigDialog: false,
    };
  },
  methods: {
    fetchSection() {
      this.loadItems();
      return service.getDraw(this.campaignId);
    },
    saveSection(body) {
      return service.saveDraw(body);
    },
    toForm(dto) {
      return {
        activeDraw: !!dto.activeDraw,
        drawModeId: dto.drawModeId || DrawModes.ByValue,
        valueDonationByNumberDraw: dto.valueDonationByNumberDraw || 0,
        productCall: dto.productCall || "",
      };
    },
    afterLoad(dto) {
      this.productsWithoutQuantity = dto.activeProductsWithoutDrawQuantity || 0;
    },
    validateForm() {
      if (this.form.activeDraw && this.form.drawModeId === DrawModes.ByValue && !(Number(this.form.valueDonationByNumberDraw) > 0)) {
        this.$store.commit("SET_SNACKBAR_MESSAGE", "Informe o valor por número de sorteio");
        return false;
      }
      return true;
    },
    buildPayload() {
      return Object.assign({}, this.form, { valueDonationByNumberDraw: Number(this.form.valueDonationByNumberDraw) || null });
    },
    loadItems() {
      return service.getProducts(this.campaignId).then((data) => {
        this.products = Object.freeze(data.products || []);
        this.gifts = Object.freeze(data.gifts || []);
        this.hasGiftConfig = !!data.hasGiftConfig;
      }).catch(() => {});
    },
    // Os modais existentes esperam o objeto completo (com perguntas e arquivo): busca pelo endpoint legado só ao editar.
    openProduct(item) {
      if (!item) {
        this.productToEdit = null;
        this.productDialog = true;
        return;
      }
      legacyService.listCampaignProductByCampaignId(this.campaignId).then((list) => {
        this.productToEdit = (list || []).find((product) => product.id === item.id) || null;
        this.productDialog = true;
      });
    },
    openGift(item) {
      if (!item) {
        this.giftToEdit = null;
        this.giftDialog = true;
        return;
      }
      legacyService.listCampaignGiftByCampaignId(this.campaignId).then((list) => {
        this.giftToEdit = (list || []).find((gift) => gift.id === item.id) || null;
        this.giftDialog = true;
      });
    },
    toggleProduct(item) {
      if (!window.confirm("Você tem certeza que deseja alterar o status deste produto?")) return;
      service.setProductStatus(this.campaignId, item.id, !item.active).then(this.loadItems);
    },
    toggleGift(item) {
      if (!window.confirm("Você tem certeza que deseja alterar o status deste brinde?")) return;
      service.setGiftStatus(this.campaignId, item.id, !item.active).then(this.loadItems);
    },
    onModalClosed(flag) {
      this[flag] = false;
      // O modal salva de forma assíncrona; recarrega a lista logo após fechar.
      setTimeout(this.loadItems, 800);
    },
    onGiftConfigClosed() {
      this.giftConfigDialog = false;
      setTimeout(this.loadItems, 800);
    },
  },
};
</script>
